/**
 * Muhurta Graha Ruler Calculator
 * Based on Bhrigu Samhita principles for calculating planetary rulers of Muhurta periods
 */

/**
 * Traditional Sanskrit planet abbreviations (2 characters)
 * Used consistently across all Vedic calculations
 */
export const VEDIC_PLANET_CODES: Record<string, string> = {
  'Sy': 'Surya',     // Sun
  'Ch': 'Chandra',   // Moon
  'Ma': 'Mangala',   // Mars
  'Bu': 'Budha',     // Mercury
  'Gu': 'Guru',      // Jupiter
  'Sk': 'Shukra',    // Venus
  'Sa': 'Shani',     // Saturn
  'Ra': 'Rahu',      // North Node
  'Ke': 'Ketu'       // South Node
};

/**
 * Reverse mapping: Full Sanskrit name to 2-character code
 */
export const VEDIC_PLANET_TO_CODE: Record<string, string> = {
  'Surya': 'Sy',
  'Chandra': 'Ch',
  'Mangala': 'Ma',
  'Budha': 'Bu',
  'Guru': 'Gu',
  'Shukra': 'Sk',
  'Shani': 'Sa',
  'Rahu': 'Ra',
  'Ketu': 'Ke'
};

/**
 * Planetary order for Muhurta rulers (Bhrigu Samhita sequence)
 * This is the traditional sequence used in calculations
 */
export const PLANETARY_ORDER: string[] = [
  'Surya', 'Shukra', 'Budha', 'Chandra', 'Shani', 'Guru', 'Mangala'
];

/**
 * Weekday to planetary lord mapping (API format)
 * Uses API vara_number directly: 0=Sunday, 1=Monday, ..., 6=Saturday
 */
export const WEEKDAY_LORDS: Record<number, string> = {
  0: 'Surya',      // Sunday (Ravivaar)
  1: 'Chandra',    // Monday (Somvaar)
  2: 'Mangala',    // Tuesday (Mangalvaar)
  3: 'Budha',      // Wednesday (Budhvaar)
  4: 'Guru',       // Thursday (Guruvaar)
  5: 'Shukra',     // Friday (Shukravaar)
  6: 'Shani'       // Saturday (Shanivaar)
};

/**
 * Calculate Muhurta Graha ruler according to Bhrigu Samhita
 *
 * According to Bhrigu Samhita:
 * - Muhurtas 1, 8, 16, 23: Ruled by the weekday lord
 * - Muhurtas 2, 9, 17, 24: Ruled by next planet in sequence
 * - Muhurtas 3, 10, 18, 25: Ruled by 2nd planet from weekday lord
 * - Continue this pattern...
 * - Muhurta 15: Always ruled by Rahu
 * - Muhurta 30: Always ruled by Ketu
 *
 * @param muhurtaNumber - Muhurta number (1-30)
 * @param varaNumber - API vara_number (0=Sunday, 1=Monday, ..., 6=Saturday)
 * @returns Name of ruling Graha
 * @throws Error if muhurtaNumber or varaNumber is out of valid range
 */
export function getMuhurtaGrahaRuler(muhurtaNumber: number, varaNumber: number): string {
  if (muhurtaNumber < 1 || muhurtaNumber > 30) {
    throw new Error(`Muhurta number must be 1-30, got ${muhurtaNumber}`);
  }

  if (varaNumber < 0 || varaNumber > 6) {
    throw new Error(`Vara number must be 0-6, got ${varaNumber}`);
  }

  // Special cases
  if (muhurtaNumber === 15) {
    return 'Rahu';
  }
  if (muhurtaNumber === 30) {
    return 'Ketu';
  }

  // Get the weekday lord using API vara_number directly
  const weekdayLord = WEEKDAY_LORDS[varaNumber];
  const weekdayLordIndex = PLANETARY_ORDER.indexOf(weekdayLord);

  if (weekdayLordIndex === -1) {
    throw new Error(`Invalid weekday lord: ${weekdayLord}`);
  }

  // Determine which group this muhurta belongs to based on the pattern
  let steps = 0;

  if ([1, 8, 16, 23].includes(muhurtaNumber)) {
    steps = 0;  // Weekday lord itself
  } else if ([2, 9, 17, 24].includes(muhurtaNumber)) {
    steps = 1;  // Next planet in sequence
  } else if ([3, 10, 18, 25].includes(muhurtaNumber)) {
    steps = 2;  // 2nd planet from weekday lord
  } else if ([4, 11, 19, 26].includes(muhurtaNumber)) {
    steps = 3;  // 3rd planet from weekday lord
  } else if ([5, 12, 20, 27].includes(muhurtaNumber)) {
    steps = 4;  // 4th planet from weekday lord
  } else if ([6, 13, 21, 28].includes(muhurtaNumber)) {
    steps = 5;  // 5th planet from weekday lord
  } else if ([7, 14, 22, 29].includes(muhurtaNumber)) {
    steps = 6;  // 6th planet from weekday lord
  }

  // Calculate ruler index using modulo arithmetic
  const rulerIndex = (weekdayLordIndex + steps) % 7;

  return PLANETARY_ORDER[rulerIndex];
}

/**
 * Get planet code from Sanskrit name
 * @param planetName - Full Sanskrit planet name
 * @returns 2-character planet code
 */
export function getPlanetCode(planetName: string): string {
  return VEDIC_PLANET_TO_CODE[planetName] || planetName.substring(0, 2);
}

/**
 * Calculate Muhurta ruler information for a specific sector
 * @param sectorNumber - Sector number (1-30)
 * @param varaNumber - API vara_number (0=Sunday, 1=Monday, ..., 6=Saturday)
 * @returns Object with ruler name and code
 */
export function getMuhurtaSectorRuler(sectorNumber: number, varaNumber: number): {
  ruler: string;
  code: string;
} {
  // Use API vara_number directly (0=Sunday, 1=Monday, ..., 6=Saturday)
  const ruler = getMuhurtaGrahaRuler(sectorNumber, varaNumber);
  const code = getPlanetCode(ruler);

  return { ruler, code };
}